<template>
  <div class="video-runs-table">
    <v-data-table
      :headers="headers"
      :items="items"
      item-key="id"
      show-select
      show-expand
      :expanded.sync="expanded"
      :value="pageSelected"
      :loading="loading"
      loading-text="Loading videos…"
      hide-default-footer
      disable-pagination
      disable-sort
      class="video-runs-table__table"
      @input="onSelectInput"
      @click:row="onRowClick"
    >
      <template #[`item.name`]="{ item }">
        <div class="d-flex align-center video-runs-table__name">
          <v-tooltip v-if="item.incoherence_label" top>
            <template #activator="{ on, attrs }">
              <v-icon
                small
                class="mr-1"
                :color="isTruthfulLabel(item) ? 'success' : 'error'"
                v-bind="attrs"
                v-on="on"
              >
                {{ isTruthfulLabel(item) ? 'mdi-check-decagram' : 'mdi-drama-masks' }}
              </v-icon>
            </template>
            <span>{{ isTruthfulLabel(item) ? 'Labeled: truthful' : 'Labeled: lie' }}</span>
          </v-tooltip>
          <span class="text-truncate video-runs-table__title" :title="displayName(item)">
            {{ displayName(item) }}
          </span>
          <span v-if="videoDuration(item)" class="caption grey--text ml-2">
            {{ videoDuration(item) }}
          </span>
        </div>
      </template>

      <template #[`item.folder`]="{ item }">
        <span class="caption">{{ folderNameOf(item) }}</span>
      </template>

      <template #[`item.created_at`]="{ item }">
        <span class="caption text-no-wrap">{{ formatDateTime(item.created_at) }}</span>
      </template>

      <template #[`item.active_run`]="{ item }">
        <div v-if="summaryOf(item)" class="d-flex align-center flex-wrap video-runs-table__active">
          <v-chip x-small label outlined class="mr-1" :title="activeRunTitle(item)">
            <v-icon v-if="summaryOf(item).active_run_id" x-small left color="success">
              mdi-star
            </v-icon>
            {{ attemptsLabel(item) }}
          </v-chip>
          <v-icon
            v-if="summaryOf(item).active_run_pinned"
            x-small
            color="info"
            title="Active run pinned"
          >
            mdi-pin
          </v-icon>
          <RunVersionChips
            v-if="lastRunOf(item) && lastRunOf(item).engine"
            :run="{ engine: lastRunOf(item).engine }"
            :source-type="sourceType"
            dense
          />
        </div>
        <span v-else class="caption grey--text">—</span>
      </template>

      <template #[`item.status`]="{ item }">
        <div class="d-flex align-center flex-wrap video-runs-table__status">
          <v-chip x-small :color="statusColor(item.status)" class="mr-1">
            {{ statusText(item.status) }}
          </v-chip>
          <RunStatusChip
            v-if="ledgerChipOf(item)"
            :status="ledgerChipOf(item).status"
            x-small
            class="mr-1"
            :title="ledgerChipOf(item).title"
          />
        </div>
      </template>

      <template #[`item.groups`]="{ item }">
        <div class="d-flex align-center flex-wrap">
          <v-chip
            v-for="membership in membershipsOf(item)"
            :key="membership.group_id"
            x-small
            outlined
            label
            class="mr-1 mb-1 video-runs-table__group-chip"
            :color="resolutionColor(membership)"
            :title="resolutionTitle(membership)"
            @click.stop="$emit('select-group', membership.group_id)"
          >
            <v-icon x-small left>{{ resolutionIcon(membership) }}</v-icon>
            {{ membership.name }}
          </v-chip>
          <span v-if="!membershipsOf(item).length" class="caption grey--text">—</span>
        </div>
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="text-no-wrap">
          <v-btn
            icon
            small
            color="primary"
            title="Open video"
            class="video-runs-table__open"
            @click.stop="$emit('open', item)"
          >
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="primary"
            title="Launch analysis"
            class="video-runs-table__launch"
            @click.stop="$emit('launch', item)"
          >
            <v-icon small>mdi-play-circle-outline</v-icon>
          </v-btn>
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" title="More actions" v-on="on" @click.stop>
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-if="groupsEnabled"
                class="video-runs-table__add-to-group"
                @click="$emit('add-to-group', item)"
              >
                <v-list-item-icon class="mr-2"><v-icon small>mdi-flask-plus-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Add to group</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="item.status === 'COMPLETED'"
                class="video-runs-table__typology"
                @click="$emit('typology', item)"
              >
                <v-list-item-icon class="mr-2"><v-icon small>mdi-counter</v-icon></v-list-item-icon>
                <v-list-item-title>Gestures by typology</v-list-item-title>
              </v-list-item>
              <v-list-item class="video-runs-table__move" @click="$emit('move', item)">
                <v-list-item-icon class="mr-2"><v-icon small>mdi-folder-move</v-icon></v-list-item-icon>
                <v-list-item-title>Move to folder</v-list-item-title>
              </v-list-item>
              <v-list-item class="video-runs-table__delete" @click="$emit('delete', item)">
                <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-delete</v-icon></v-list-item-icon>
                <v-list-item-title class="error--text">Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template #expanded-item="{ headers: expandedHeaders, item }">
        <td :colspan="expandedHeaders.length" class="video-runs-table__expanded px-4">
          <VideoRunRow
            :item="item"
            :source-type="sourceType"
            :summary="summaryOf(item)"
            :memberships="membershipsOf(item)"
            :groups="groups"
            @compare="openCompare"
            @changed="onRunChanged(item, $event)"
            @membership-changed="onMembershipChanged(item, $event)"
            @notify="$emit('notify', $event)"
            @launch="$emit('launch', item)"
          />
        </td>
      </template>

      <template #no-data>
        <div class="py-6 text-center">
          <v-icon size="36" color="grey">mdi-file-video-outline</v-icon>
          <p class="mt-2 mb-0 grey--text text--darken-1">No videos found on this page.</p>
        </div>
      </template>
    </v-data-table>

    <RunCompareDialog v-model="compareOpen" :a="compareA" :b="compareB" />
  </div>
</template>

<script>
import RunCompareDialog from '@/components/analysis/runs/RunCompareDialog.vue';
import RunStatusChip from '@/components/analysis/runs/RunStatusChip.vue';
import RunVersionChips from '@/components/analysis/runs/RunVersionChips.vue';
import featureFlags from '@/config/featureFlags';
import { bucketPresentation } from '@/utils/groupResolution';
import { formatDateTime, ledgerStatusOf } from './runRowHelpers';
import { fetchSourceMemberships, fetchSourceRunSummaries } from './sourceRunSummaries';
import VideoRunRow from './VideoRunRow.vue';

const LIVE_STATUSES = ['QUEUED', 'DISPATCHED', 'RUNNING', 'CANCEL_REQUESTED'];

/**
 * Table mode of the manual video list (M4 F13): one row per video with its SQL status, the
 * ledger status of its last run, the active run roll-up, group membership chips and actions;
 * expanding a row shows its runs by group (VideoRunRow). Loads the ledger summary and the
 * memberships of the visible page itself and re-reads them when `refreshToken` changes.
 *
 * Selection is shared with the parent through `selected` (objects, may include videos of other
 * pages) and `update:selected`.
 *
 * Emits: `update:selected`, `open`, `launch`, `add-to-group`, `typology`, `move`, `delete`,
 * `select-group`, `notify` `{ message, type }`, `changed` (a run action happened),
 * `membership-changed` `{ source_id, group_id, action }`.
 */
export default {
  name: 'VideoRunsTable',

  components: { RunCompareDialog, RunStatusChip, RunVersionChips, VideoRunRow },

  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** Selected videos (objects with at least `id`). */
    selected: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    folders: { type: Array, default: () => [] },
    sourceType: { type: String, default: 'manual_analysis' },
    /** Bump to re-read summaries and memberships (after launches, socket events, ...). */
    refreshToken: { type: Number, default: 0 },
  },

  data() {
    return {
      expanded: [],
      summaries: {},
      memberships: {},
      compareOpen: false,
      compareA: null,
      compareB: null,
    };
  },

  computed: {
    groupsEnabled() {
      return Boolean(featureFlags.analysisGroups);
    },
    headers() {
      const headers = [
        { text: 'Video', value: 'name', sortable: false },
        { text: 'Folder', value: 'folder', sortable: false, width: 140 },
        { text: 'Uploaded', value: 'created_at', sortable: false, width: 140 },
        { text: 'Active run', value: 'active_run', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
      ];
      if (this.groupsEnabled) headers.push({ text: 'Groups', value: 'groups', sortable: false });
      headers.push({ text: 'Actions', value: 'actions', sortable: false, align: 'end', width: 150 });
      return headers;
    },
    pageIds() {
      return new Set(this.items.map((item) => Number(item?.id)));
    },
    pageSelected() {
      const selectedIds = new Set(this.selected.map((item) => Number(item?.id)));
      return this.items.filter((item) => selectedIds.has(Number(item.id)));
    },
    folderNames() {
      const map = {};
      this.folders.forEach((folder) => {
        if (folder && folder.id != null) map[String(folder.id)] = folder.name;
      });
      return map;
    },
  },

  watch: {
    items: {
      handler() {
        this.expanded = this.expanded.filter((row) => this.pageIds.has(Number(row?.id)));
        this.loadMeta();
      },
      immediate: true,
    },
    refreshToken() {
      this.loadMeta();
    },
  },

  methods: {
    formatDateTime,

    async loadMeta() {
      const ids = this.items.map((item) => item.id);
      if (!ids.length) {
        this.summaries = {};
        this.memberships = {};
        return;
      }
      const [summaries, memberships] = await Promise.all([
        fetchSourceRunSummaries(this.sourceType, ids),
        this.groupsEnabled ? fetchSourceMemberships(this.sourceType, ids) : Promise.resolve({}),
      ]);
      this.summaries = summaries;
      this.memberships = memberships;
    },

    async refreshSource(sourceId) {
      const [summaries, memberships] = await Promise.all([
        fetchSourceRunSummaries(this.sourceType, [sourceId]),
        this.groupsEnabled
          ? fetchSourceMemberships(this.sourceType, [sourceId])
          : Promise.resolve({}),
      ]);
      this.summaries = { ...this.summaries, ...summaries };
      if (this.groupsEnabled) {
        const next = { ...this.memberships };
        next[String(sourceId)] = memberships[String(sourceId)] || [];
        this.memberships = next;
      }
    },

    summaryOf(item) {
      return this.summaries[String(item?.id)] || null;
    },
    lastRunOf(item) {
      return this.summaryOf(item)?.last_run || null;
    },
    membershipsOf(item) {
      return this.memberships[String(item?.id)] || [];
    },
    attemptsLabel(item) {
      const n = Number(this.summaryOf(item)?.attempts || 0);
      return `${n} ${n === 1 ? 'run' : 'runs'}`;
    },
    activeRunTitle(item) {
      const summary = this.summaryOf(item);
      if (!summary?.active_run_id) return 'No active run';
      return `Active run ${String(summary.active_run_id).slice(0, 8)}`;
    },
    ledgerChipOf(item) {
      const ledger = ledgerStatusOf(this.summaryOf(item));
      if (!ledger) return null;
      const live = LIVE_STATUSES.includes(ledger.status);
      return { status: ledger.status, title: live ? 'A run is in the analysis queue' : 'Last run' };
    },
    resolutionColor(membership) {
      return bucketPresentation(membership?.resolution || null).color;
    },
    resolutionIcon(membership) {
      return bucketPresentation(membership?.resolution || null).icon;
    },
    resolutionTitle(membership) {
      const label = bucketPresentation(membership?.resolution || null).label;
      return `${membership.name}: ${label.toLowerCase()} (click to filter)`;
    },
    folderNameOf(item) {
      const id = item?.id_folder ?? item?.folder_id ?? item?.folderId;
      if (id == null) return 'Unassigned';
      return this.folderNames[String(id)] || item?.folder_name || `Folder #${id}`;
    },
    displayName(item) {
      return item?.name || item?.original_filename || `Analysis ${item?.id}`;
    },
    isTruthfulLabel(item) {
      return item?.incoherence_label?.label_type === 'truthful';
    },
    videoDuration(item) {
      const candidates = [
        item?.video_duration_seconds,
        item?.videoDurationSeconds,
        item?.duration_seconds,
        item?.video_duration,
        item?.duration,
      ];
      for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value) && value > 0) {
          const total = Math.round(value);
          return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
        }
      }
      return '';
    },
    statusColor(status) {
      if (status === 'COMPLETED') return 'success';
      if (status === 'ANALYSING') return 'primary';
      if (status === 'UPLOADING') return 'info';
      if (status === 'ERROR') return 'error';
      return 'grey';
    },
    statusText(status) {
      if (status === 'COMPLETED') return 'Completed';
      if (status === 'ANALYSING') return 'Analysing...';
      if (status === 'UPLOADING') return 'Uploading...';
      if (status === 'ERROR') return 'Error';
      return status;
    },

    onSelectInput(pageItems) {
      const kept = this.selected.filter((item) => !this.pageIds.has(Number(item?.id)));
      this.$emit('update:selected', [...kept, ...pageItems]);
    },
    onRowClick(item, slot) {
      if (slot && typeof slot.expand === 'function') slot.expand(!slot.isExpanded);
    },
    openCompare({ a, b }) {
      this.compareA = a || null;
      this.compareB = b || null;
      this.compareOpen = Boolean(a && b);
    },
    onRunChanged(item, event) {
      this.refreshSource(item.id);
      this.$emit('changed', { source_id: item.id, ...(event || {}) });
    },
    onMembershipChanged(item, event) {
      this.refreshSource(item.id);
      this.$emit('membership-changed', { source_id: item.id, ...(event || {}) });
    },
  },
};
</script>

<style scoped>
.video-runs-table__title {
  max-width: 260px;
  display: inline-block;
}

.video-runs-table__expanded {
  background: #fafafa;
}

.video-runs-table__group-chip {
  cursor: pointer;
}

.video-runs-table >>> tbody tr {
  cursor: pointer;
}
</style>
